import React from 'react';
import {
  Button,
  Column,
  Copy,
  Flex,
  Heading,
  Row,
  Square,
  Stack,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Input } from 'src/components/Input/Input';
import { Modal } from 'src/components/Modal/Modal';
import {
  getFormattedPhoneNumber,
  PhoneMaskInput,
  removePhoneNumberFormatting,
} from 'src/components/PhoneMaskInput/PhoneMaskInput';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import {
  addInvoiceContact,
  IInvoiceContact,
  InvoiceContactAPIProps,
  InvoiceContactPayload,
  SubtotalInvoiceMethod,
  updateInvoiceContact,
} from 'src/utilities/invoiceContact.utils';
import { EMAIL_REGEX } from 'src/utilities/regexPatterns';

interface IEnterpriseInvoiceRecipientModal {
  invoiceContact?: IInvoiceContact;
  onClose: () => void;
  onSuccess: () => void;
  corpId: string;
}

type InvoiceContactFormFields = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

const DEFAULT_INVOICE_CONTACT_FORM_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
};

export function EnterpriseInvoiceRecipientModal({
  invoiceContact,
  onClose,
  onSuccess,
  corpId,
}: IEnterpriseInvoiceRecipientModal): JSX.Element {
  const { corpData } = useAuth();
  const isEditing = !!invoiceContact;
  const { handleSubmit, register, errors, formState, getValues, control } =
    useForm<InvoiceContactFormFields>({
      mode: 'all',
      defaultValues: isEditing
        ? {
            firstName: invoiceContact.first_name,
            lastName: invoiceContact.last_name,
            email: invoiceContact.email,
            phoneNumber: getFormattedPhoneNumber(invoiceContact.phone),
          }
        : DEFAULT_INVOICE_CONTACT_FORM_VALUES,
    });
  const { isSubmitting, isValid } = formState;
  const areLocationsRequired = corpData.locationsRequired;
  const areDepartmentsRequired = corpData.departmentsRequired;

  async function onFormSubmit({
    firstName,
    lastName,
    email = getValues('email'),
    phoneNumber,
  }: InvoiceContactFormFields): Promise<void> {
    const contact: InvoiceContactPayload = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone: removePhoneNumberFormatting(phoneNumber),
    };
    const fullContactName = `${firstName} ${lastName}`;
    const actionText = isEditing ? 'updated' : 'added';
    const payload: InvoiceContactAPIProps = {
      corpId,
      subtotalMethod: SubtotalInvoiceMethod.MEAL_TYPE,
      payload: contact,
    };

    try {
      if (isEditing) {
        payload.invoiceContactId = invoiceContact.invoice_contact_id;
        await updateInvoiceContact(payload);
      } else {
        await addInvoiceContact(payload);
      }

      pushSuccess(`${fullContactName} was ${actionText}`);
      onClose();
      onSuccess();
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: `${fullContactName} was not ${actionText}. Try again.`,
      });
    }
  }

  function getInfoMessage(): string {
    if (areDepartmentsRequired) {
      return 'This person will receive all department invoices.';
    }
    if (areLocationsRequired) {
      return 'This person will receive all location invoices.';
    }
    return 'This person will receive all invoices.';
  }

  return (
    <Modal size="sm">
      <Square as="header" dimension="2" width="100%">
        <Button variant="tertiary" type="button" onClick={onClose}>
          <SvgX size="24px" />
        </Button>
        <Square inset="md">
          <Heading variant="h1">
            {isEditing
              ? 'Edit invoice recipient info'
              : 'Add invoice recipient'}
          </Heading>
        </Square>
      </Square>
      <Square inset="md" position="relative">
        <form>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <Stack y="md">
                <Input
                  label="First name *"
                  name="firstName"
                  id="firstName"
                  maxLength={60}
                  ref={register({
                    required: 'First name is required',
                  })}
                  error={errors.firstName}
                />
              </Stack>
            </Column>
            <Column gutter={12} phone={6}>
              <Stack y="md">
                <Input
                  label="Last name *"
                  name="lastName"
                  id="lastName"
                  maxLength={60}
                  ref={register({
                    required: 'Last name is required',
                  })}
                  error={errors.lastName}
                />
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <Stack y="md">
                <Input
                  label="Email *"
                  name="email"
                  id="email"
                  type="email"
                  ref={register({
                    required: 'Email is required',
                    pattern: {
                      value: EMAIL_REGEX,
                      message: 'Email is invalid',
                    },
                  })}
                  error={errors.email}
                />
              </Stack>
            </Column>
            <Column gutter={12} phone={6}>
              <Stack y="md">
                <PhoneMaskInput
                  label="Phone no. *"
                  id="phoneNumber"
                  name="phoneNumber"
                  required
                  control={control}
                  error={errors.phoneNumber}
                />
              </Stack>
            </Column>
          </Row>
        </form>
        <Stack y="xx-sm">
          <Copy variant="body" color="secondaryText">
            {getInfoMessage()}
          </Copy>
        </Stack>
      </Square>
      <Square as="footer" dimension="2" inset="md">
        <Flex>
          <Stack x="xx-sm">
            <Button
              variant="primary"
              buttonSize="medium"
              disabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleSubmit(onFormSubmit)}
            >
              {isEditing ? 'Update' : 'Add'}
            </Button>
          </Stack>

          <Button
            variant="secondary"
            buttonSize="medium"
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
          >
            Cancel
          </Button>
        </Flex>
      </Square>
    </Modal>
  );
}
